/**
 * Half-hour clock used by the booking TimePicker, and the one way a chosen
 * time is reconciled with a day's window.
 *
 * The picker draws every slot and greys the ones outside `minTime`/`maxTime`.
 * That is display. This file is the value: given a stored "HH:MM" and a
 * window, what should the field actually hold?
 *
 * iOS `UIDatePicker` clamps to `minimumDate`/`maximumDate`. It never clears
 * the selection and never shows an illegal time as current. Same contract
 * here. Clearing is reserved for the case where the window contains no slot
 * at all (min after 23:30, max before 00:00).
 *
 * String compare on zero-padded 24h "HH:MM" is chronological. Bounds from
 * the server (`free_from` / `free_until`) may fall between slots; the clamp
 * then walks to the first legal chip.
 */

export const TIME_SLOTS: readonly string[] = (() => {
  const slots: string[] = []
  for (let hour = 0; hour < 24; hour += 1) {
    for (const minute of [0, 30]) {
      slots.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`)
    }
  }
  return slots
})()

export const HOURLY_TIME_SLOTS: readonly string[] = TIME_SLOTS.filter((time) =>
  time.endsWith(':00'),
)

/** Format "HH:MM" (24h) to a 12h label: "9:00 AM", "1:30 PM". */
export function formatTime12h(time: string): string {
  const [hourToken, minuteToken] = time.split(':')
  const hour = parseInt(hourToken ?? '', 10)
  if (!Number.isFinite(hour) || minuteToken == null) return time
  const suffix = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
  return `${displayHour}:${minuteToken} ${suffix}`
}

export function isTimeInWindow(time: string, minTime?: string, maxTime?: string): boolean {
  if (minTime !== undefined && time < minTime) return false
  if (maxTime !== undefined && time > maxTime) return false
  return true
}

export function firstSlotOnOrAfter(
  bound: string,
  slots: readonly string[] = TIME_SLOTS,
): string | null {
  return slots.find((slot) => slot >= bound) ?? null
}

/**
 * First selectable slot strictly later than `bound`.
 *
 * Booking uses this for the current half-hour after a few real seconds have
 * elapsed. A `09:30` pickup at 09:30:17 is already server-invalid even though
 * its `HH:MM` string compares equal to the clock, so treating equality as a
 * future slot creates a checkout that can never be paid for.
 */
export function firstSlotAfter(
  bound: string,
  slots: readonly string[] = TIME_SLOTS,
): string | null {
  return slots.find((slot) => slot > bound) ?? null
}

export function lastSlotOnOrBefore(
  bound: string,
  slots: readonly string[] = TIME_SLOTS,
): string | null {
  for (let index = slots.length - 1; index >= 0; index -= 1) {
    const slot = slots[index]
    if (slot && slot <= bound) return slot
  }
  return null
}

/**
 * Reconcile a stored time with the day's window.
 *
 * - `null` stays `null`. An empty field is "not chosen yet", not "snap to min".
 * - Inside the window, the value is unchanged.
 * - Below `minTime`, the first legal slot. Above `maxTime`, the last.
 * - No legal slot: `null`, so the field goes empty and the picker can say so.
 */
export function clampTimeToWindow(
  time: string | null,
  minTime?: string,
  maxTime?: string,
): string | null {
  if (!time) return null
  if (isTimeInWindow(time, minTime, maxTime)) return time
  if (minTime !== undefined && time < minTime) {
    const snapped = firstSlotOnOrAfter(minTime)
    if (snapped && isTimeInWindow(snapped, minTime, maxTime)) return snapped
    return null
  }
  if (maxTime !== undefined && time > maxTime) {
    const snapped = lastSlotOnOrBefore(maxTime)
    if (snapped && isTimeInWindow(snapped, minTime, maxTime)) return snapped
    return null
  }
  return null
}

/**
 * Desktop and tablet never inherit the phone chip carousel. Phone (`isMobile`)
 * may keep the strip; everything at `md` and up — including iPad landscape —
 * uses the compact menu. `isDesktop` is large web only, so tablet must count.
 */
export function timeSlotPickerUsesMenu(isDesktop: boolean, isTablet: boolean): boolean {
  return isDesktop || isTablet
}

/** AM is before noon; PM is noon onward. Both 30-minute and hourly clocks split evenly. */
export function splitTimeSlotsByMeridiem(slots: readonly string[]): {
  am: string[]
  pm: string[]
} {
  const am: string[] = []
  const pm: string[] = []
  for (const slot of slots) {
    if (slot < '12:00') am.push(slot)
    else pm.push(slot)
  }
  return { am, pm }
}

export type TimeSlotKeyboardDirection = 'up' | 'down' | 'left' | 'right' | 'home' | 'end'

function nearestEnabledFrom(
  slots: readonly string[],
  index: number,
  minTime?: string,
  maxTime?: string,
): string | null {
  const at = slots[index]
  if (at && isTimeInWindow(at, minTime, maxTime)) return at
  const after = slots
    .slice(Math.max(0, index))
    .find((slot) => isTimeInWindow(slot, minTime, maxTime))
  if (after) return after
  for (let i = Math.min(index, slots.length - 1); i >= 0; i -= 1) {
    const slot = slots[i]
    if (slot && isTimeInWindow(slot, minTime, maxTime)) return slot
  }
  return null
}

/**
 * Two-column menu keyboard: up/down stay on AM or PM, left/right jump the
 * matching row (9:00 AM ↔ 9:00 PM). Disabled window hours are skipped, never
 * selected. Home/End are the first and last legal slot in the whole day.
 */
export function stepTimeSlotKeyboard(
  slots: readonly string[],
  current: string | null,
  direction: TimeSlotKeyboardDirection,
  minTime?: string,
  maxTime?: string,
): string | null {
  const enabled = slots.filter((slot) => isTimeInWindow(slot, minTime, maxTime))
  if (enabled.length === 0) return null
  if (direction === 'home') return enabled[0] ?? null
  if (direction === 'end') return enabled[enabled.length - 1] ?? null

  const { am, pm } = splitTimeSlotsByMeridiem(slots)
  const start =
    current && isTimeInWindow(current, minTime, maxTime) ? current : (enabled[0] ?? null)
  if (!start) return null

  const inAm = am.includes(start)
  const column = inAm ? am : pm
  const other = inAm ? pm : am
  const index = column.indexOf(start)
  if (index < 0) return start

  if (direction === 'left' || direction === 'right') {
    const crossing = direction === 'right' ? inAm : !inAm
    if (!crossing) return start
    return nearestEnabledFrom(other, index, minTime, maxTime) ?? start
  }

  const step = direction === 'down' ? 1 : -1
  for (let i = index + step; i >= 0 && i < column.length; i += step) {
    const candidate = column[i]
    if (candidate && isTimeInWindow(candidate, minTime, maxTime)) return candidate
  }
  return start
}

/**
 * Slot the chip strip should scroll to: the current legal value, otherwise
 * the start of a pickup window or the end of a return window, so the first
 * thing on screen is a tappable hour rather than a run of grey midnights.
 */
export function timePickerScrollSlot(
  value: string | null,
  minTime?: string,
  maxTime?: string,
  slots: readonly string[] = TIME_SLOTS,
): string | null {
  if (value && isTimeInWindow(value, minTime, maxTime) && slots.includes(value)) {
    return value
  }
  if (minTime !== undefined) {
    const first = firstSlotOnOrAfter(minTime, slots)
    if (first && isTimeInWindow(first, minTime, maxTime)) return first
  }
  if (maxTime !== undefined) {
    const last = lastSlotOnOrBefore(maxTime, slots)
    if (last && isTimeInWindow(last, minTime, maxTime)) return last
  }
  return null
}
